import { NavLink, useNavigate } from "react-router-dom";
import { Home, Radio, Users, Bell, User, LogOut, Feather, Video, MapPin, Shield, Coins, MessageSquare, PlaySquare, Ticket, BarChart3, HelpCircle, Share2, DollarSign, Crown, Scale } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { NAV, LOGOUT } from "@/constants/testIds";
import TrialCountdown from "./TrialCountdown";
import SidePill from "./SidePill";
import { BecomeCreatorButton } from "./CreatorModeToggle";
import useUnreadDMs from "@/hooks/useUnreadDMs";
import UnreadDot from "@/components/inbox/UnreadDot";

const items = [
  { to: "/feed", label: "Feed", icon: Home, tid: NAV.feedLink },
  { to: "/members", label: "Members", icon: Users, tid: "nav-members-link" },
  { to: "/live", label: "Live", icon: Radio, tid: NAV.liveLink },
  { to: "/inbox", label: "Inbox", icon: MessageSquare, tid: "nav-inbox-link" },
  { to: "/nearby", label: "Social Meet", icon: MapPin, tid: "nav-nearby-link" },
  { to: "/communities", label: "Communities", icon: Users, tid: NAV.communitiesLink },
  { to: "/referrals", label: "Invite Friends", icon: Share2, tid: "nav-invite-friends-link" },
  { to: "/earnings", label: "Earnings & Payouts", icon: DollarSign, tid: "nav-earnings-link" },
  { to: "/vip", label: "VIP & Badges", icon: Crown, tid: "nav-vip-link" },
  { to: "/notifications", label: "Notifications", icon: Bell, tid: NAV.notificationsLink },
  { to: "/tutorial", label: "How to use", icon: HelpCircle, tid: "nav-tutorial-link" },
  { to: "/legal", label: "Legal & Support", icon: Scale, tid: "nav-legal-link" },
];

export default function LeftRail({ onCompose, onGoLive, onTicket }) {
  const { total: unreadDMs } = useUnreadDMs();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside className="lg:col-span-3 lg:sticky lg:top-0 lg:h-screen py-8 pr-4 hidden lg:flex flex-col justify-between">
      <div>
        <NavLink to="/feed" data-testid={NAV.logoLink} className="flex items-center gap-3 mb-10 group">
          <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center text-lg font-black text-white">
            🐼
          </div>
          <span className="font-display text-2xl tracking-tight">My Panda X</span>
        </NavLink>

        <nav className="flex flex-col gap-1">
          {items.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-none border-l-2 transition-colors duration-200 ${
                  isActive
                    ? "border-rose-600 text-white bg-white/[0.03]"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.02]"
                }`
              }
            >
              <span className="relative"><Icon size={20} strokeWidth={1.6} />{to === "/inbox" && <UnreadDot count={unreadDMs} className="-right-2.5" />}</span>
              <span className="text-sm tracking-wide">{label}</span>
            </NavLink>
          ))}

          {user && (
            <NavLink
              to={`/u/${user.id}`}
              data-testid={NAV.profileLink}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-none border-l-2 transition-colors duration-200 ${
                  isActive
                    ? "border-rose-600 text-white bg-white/[0.03]"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.02]"
                }`
              }
            >
              <User size={20} strokeWidth={1.6} />
              <span className="text-sm tracking-wide">Profile</span>
            </NavLink>
          )}

          {(user?.is_creator || user?.role === "admin") && (
            <NavLink
              to="/creator"
              data-testid="nav-creator-link"
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-none border-l-2 transition-colors duration-200 ${
                  isActive
                    ? "border-rose-600 text-white bg-white/[0.03]"
                    : "border-transparent text-amber-400 hover:text-white hover:bg-white/[0.02]"
                }`
              }
            >
              <BarChart3 size={20} strokeWidth={1.6} />
              <span className="text-sm tracking-wide">Creator</span>
            </NavLink>
          )}

          {user && !(user.is_creator || user.role === "admin") && <BecomeCreatorButton />}

          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin-link"
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-none border-l-2 transition-colors duration-200 ${
                  isActive
                    ? "border-rose-600 text-white bg-white/[0.03]"
                    : "border-transparent text-rose-500 hover:text-white hover:bg-white/[0.02]"
                }`
              }
            >
              <Shield size={20} strokeWidth={1.6} />
              <span className="text-sm tracking-wide">Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="flex flex-col gap-3 mt-8">
          <Button
            data-testid={NAV.composeButton}
            onClick={onCompose}
            className="btn-crimson w-full rounded-none py-6 font-medium tracking-wide flex items-center gap-2"
          >
            <Feather size={18} /> Post
          </Button>
          <Button
            data-testid={NAV.goLiveButton}
            onClick={onGoLive}
            variant="outline"
            className="w-full rounded-none py-6 bg-transparent border border-white/15 hover:bg-white/[0.04] hover:border-rose-600/60 text-white flex items-center gap-2"
          >
            <Video size={18} /> Go Live
          </Button>
          {user && onTicket && (
            <Button
              data-testid="nav-daily-ticket"
              onClick={onTicket}
              variant="outline"
              className="w-full rounded-none py-6 bg-transparent border border-amber-400/40 hover:border-amber-400 text-amber-400 flex items-center gap-2"
            >
              <Ticket size={18} /> Daily Ticket
            </Button>
          )}
        </div>
      </div>

      {user && (
        <div className="mt-6 pt-6 border-t border-white/5">
          <SidePill className="w-full justify-between mb-3" />
          <TrialCountdown variant="compact" className="mb-3" />
          <NavLink
            to="/coins"
            data-testid="nav-coins-link"
            className={({ isActive }) =>
              `flex items-center justify-between px-4 py-3 border-l-2 mb-3 transition-colors duration-200 ${
                isActive
                  ? "border-amber-400 bg-white/[0.03]"
                  : "border-transparent hover:bg-white/[0.02]"
              }`
            }
          >
            <span className="flex items-center gap-3">
              <Coins size={16} className="text-amber-400" />
              <span className="text-sm">{(user.coins ?? 0).toLocaleString()} coins</span>
            </span>
            <span className="text-xs text-rose-500">+ Top up</span>
          </NavLink>
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="w-10 h-10 border border-white/10">
              <AvatarImage src={user.avatar_url} alt={user.handle} />
              <AvatarFallback className="bg-rose-600 text-white text-sm">
                {user.display_name?.[0]?.toUpperCase() || "P"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.display_name}</div>
              <div className="text-xs text-zinc-500 truncate">{user.display_name}</div>
            </div>
          </div>
          <button
            data-testid={LOGOUT.button}
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="w-full flex items-center gap-2 text-sm text-zinc-500 hover:text-rose-500 transition-colors"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      )}
    </aside>
  );
}
